import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';
import { getUserRole, hasPermission } from '../utils/roleUtils';
import { useAuth } from '../contexts/AuthContext';
import { useScope } from '../contexts/ScopeContext';
import authenticatedFetch from '../utils/authenticatedFetch';

interface Discount {
  discountId: string;
  selectedEnterpriseId: string;
  name: string;
  description: string;
  type: 'percentage' | 'fixed_amount' | 'buy_one_get_one' | 'free_item';
  value: number;
  
  // API field names for backward compatibility
  discountType?: 'percentage' | 'fixed_amount' | 'buy_one_get_one' | 'free_item';
  discountValue?: number;
  
  // Time restrictions
  timeRestrictions: {
    enabled: boolean;
    startTime: string | null;
    endTime: string | null;
    daysOfWeek: number[];
    dateRange: {
      start: string | null;
      end: string | null;
    };
  };
  
  // Authorization
  requiresAuthorization: boolean;
  authorizationLevel: 'none' | 'manager' | 'system_admin' | 'super_admin';
  
  // General Ledger
  glCode: string;
  glDescription: string;
  
  // Combination rules
  canCombine: boolean;
  combinationRules: string[];
  maxCombinations: number;
  
  // Assignment levels
  assignmentLevels: string[];
  
  // Trigger settings
  triggerType: 'manual' | 'time_based' | 'loyalty' | 'ai_triggered' | 'event_based';
  triggerConditions: any;
  
  // Loyalty integration
  loyaltyIntegration: {
    enabled: boolean;
    pointsRequired: number;
    tierRequired: string | null;
    minimumPurchase: number;
  };
  
  // Table restrictions
  tableRestrictions: {
    enabled: boolean;
    allowedAreas: string[];
    allowedTables: string[];
    excludedAreas: string[];
    excludedTables: string[];
  };
  
  // Item and Category restrictions
  itemRestrictions: {
    enabled: boolean;
    restrictionType: 'none' | 'items' | 'categories' | 'both';
    allowedItems: string[]; // Array of menu item IDs
    excludedItems: string[]; // Array of menu item IDs to exclude
    allowedCategories: string[]; // Array of category IDs
    excludedCategories: string[]; // Array of category IDs to exclude
    minimumItemQuantity: number | null; // Minimum quantity of qualifying items
    maximumItemQuantity: number | null; // Maximum quantity of qualifying items
  };
  
  // AI triggers
  aiTriggerSettings: {
    enabled: boolean;
    apiEndpoint: string | null;
    triggerEvents: string[];
    conditions: any;
  };
  
  // Usage limits
  usageLimits: {
    maxUsesPerCustomer: number | null;
    maxUsesPerDay: number | null;
    maxUsesTotal: number | null;
    currentUsage: number;
  };
  
  // Metadata
  status: 'active' | 'inactive' | 'deleted';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  version: number;
}

interface DiscountAssignment {
  assignmentId: string;
  discountId: string;
  selectedEnterpriseId: string;
  assignmentLevel: 'concept' | 'franchise' | 'org_unit' | 'store' | 'table';
  targetId: string;
  targetName: string;
  isActive: boolean;
  assignedAt: string;
  assignedBy: string;
  restrictions: any;
  priority: number;
  
  // Effective date functionality
  effectiveDate: string; // When assignment becomes active
  expirationDate: string | null; // When assignment expires (null = no expiration)
  timezone: string; // Timezone for date calculations
  
  // Hierarchy inheritance
  inheritFromParent: boolean; // Whether to inherit from parent hierarchy level
  overrideParent: boolean; // Whether this assignment overrides parent assignments
  
  // Usage tracking
  usageCount: number;
  lastUsed: string | null;
}

interface DiscountManagementProps {
  user: any;
}

const DiscountManagement: React.FC<DiscountManagementProps> = ({ user }) => {
  const { session } = useAuth();
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [selectedDiscount, setSelectedDiscount] = useState<Discount | null>(null);
  const [assignments, setAssignments] = useState<DiscountAssignment[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState({
    status: '',
    triggerType: '',
    assignmentLevel: ''
  });
  
  // Enterprise selection
  const { enterpriseId: scopeEnterpriseId } = useScope();
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState<string>(scopeEnterpriseId || '');
  const [userPickedEnterprise, setUserPickedEnterprise] = useState(false);

  // Follow the header enterprise selector until the user picks locally.
  useEffect(() => {
    if (userPickedEnterprise || !scopeEnterpriseId) return;
    if (selectedEnterpriseId === scopeEnterpriseId) return;
    if (enterprises.length > 0 && !enterprises.some((e: any) => e.enterpriseId === scopeEnterpriseId)) return;
    setSelectedEnterpriseId(scopeEnterpriseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEnterpriseId, userPickedEnterprise, enterprises]);

  const userRole = getUserRole(user, session);
  const canManageDiscounts = hasPermission(user, 'canManageDiscounts', session);

  // Load enterprises on component mount
  useEffect(() => {
    loadEnterprises();
  }, []);

  const loadEnterprises = async () => {
    try {
      console.log('🏢 [DiscountManagement] Loading enterprises...');
      const response = await authenticatedFetch(API_ENDPOINTS.enterprises());
      if (response.ok) {
        const data = await response.json();
        console.log('🏢 [DiscountManagement] Enterprises loaded:', data);
        setEnterprises(data.data || data || []);
      } else {
        console.error('🏢 [DiscountManagement] Failed to load enterprises:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('🏢 [DiscountManagement] Network error loading enterprises:', error);
    }
  };

  // Transform API discount data to match our interface
  const normalizeDiscount = (discount: any): Discount => {
    return {
      ...discount,
      type: discount.type || discount.discountType || 'percentage',
      value: discount.value || discount.discountValue || 0,
      timeRestrictions: discount.timeRestrictions || {
        enabled: false,
        startTime: null,
        endTime: null,
        daysOfWeek: [],
        dateRange: {
          start: null,
          end: null
        }
      },
      status: discount.status || (discount.isActive ? 'active' : 'inactive'),
      glCode: discount.glCode || '',
      canCombine: discount.canCombine || discount.stackable || false,
      requiresAuthorization: discount.requiresAuthorization || false,
      authorizationLevel: discount.authorizationLevel || 'none',
      itemRestrictions: discount.itemRestrictions || {
        enabled: false,
        restrictionType: 'none',
        allowedItems: [],
        excludedItems: [],
        allowedCategories: [],
        excludedCategories: [],
        minimumItemQuantity: null,
        maximumItemQuantity: null
      }
    };
  };

  useEffect(() => {
    if (canManageDiscounts && selectedEnterpriseId) {
      loadDiscounts();
    }
  }, [canManageDiscounts, filter, selectedEnterpriseId]);

  const loadDiscounts = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (filter.status) queryParams.append('status', filter.status);
      if (filter.triggerType) queryParams.append('triggerType', filter.triggerType);
      if (filter.assignmentLevel) queryParams.append('assignmentLevel', filter.assignmentLevel);

      const url = `${API_ENDPOINTS.enterpriseDiscounts(selectedEnterpriseId)}?${queryParams}`;
      console.log('[DiscountManagement] Loading discounts from:', url);

      const response = await authenticatedFetch(url);

      console.log('[DiscountManagement] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[DiscountManagement] Error response:', errorText);
        throw new Error('Failed to load discounts');
      }

      const data = await response.json();
      console.log('[DiscountManagement] Raw API response:', data);
      console.log('[DiscountManagement] data.data:', data.data);
      console.log('[DiscountManagement] data.discounts:', data.discounts);

      const discountsArray = data.data || data.discounts || [];
      console.log('[DiscountManagement] Discounts array to process:', discountsArray);

      setDiscounts(discountsArray.map(normalizeDiscount));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load discounts');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async (discountId?: string) => {
    try {
      console.log('Loading assignments for enterprise:', selectedEnterpriseId, 'discountId:', discountId);
      const response = await authenticatedFetch(
        API_ENDPOINTS.discountAssignments(selectedEnterpriseId)
      );

      if (!response.ok) throw new Error('Failed to load assignments');
      
      const data = await response.json();
      console.log('Raw assignments response:', data);
      const allAssignments = (data.assignments || []).filter((a: any) => a && a.assignmentId);
      console.log('Filtered assignments:', allAssignments);
      
      // Filter by discount if specified
      const filteredAssignments = discountId 
        ? allAssignments.filter((a: DiscountAssignment) => a.discountId === discountId)
        : allAssignments;
      
      console.log('Final assignments to set:', filteredAssignments);
      setAssignments(filteredAssignments);
    } catch (err) {
      console.error('Error loading assignments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
    }
  };

  const handleCreateDiscount = async (discountData: Partial<Discount>) => {
    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.createDiscount(selectedEnterpriseId),
        {
          method: 'POST',
          body: JSON.stringify({
            ...discountData,
            createdBy: user?.getUsername?.() || 'system'
          })
        }
      );

      if (!response.ok) throw new Error('Failed to create discount');
      
      const data = await response.json();
      setDiscounts(prev => [normalizeDiscount(data.data), ...prev]);
      setShowCreateModal(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create discount');
    }
  };

  const handleUpdateDiscount = async (discountId: string, updates: Partial<Discount>) => {
    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.updateDiscount(discountId),
        {
          method: 'PUT',
          body: JSON.stringify(updates)
        }
      );

      if (!response.ok) throw new Error('Failed to update discount');
      
      const data = await response.json();
      setDiscounts(prev => prev.map(d => d.discountId === discountId ? normalizeDiscount(data.data) : d));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update discount');
    }
  };

  const handleDeleteDiscount = async (discountId: string) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) return;

    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.deleteDiscount(discountId),
        {
          method: 'DELETE'
        }
      );

      if (!response.ok) throw new Error('Failed to delete discount');
      
      setDiscounts(prev => prev.filter(d => d.discountId !== discountId));
      setSelectedDiscount(null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete discount');
    }
  };

  const handleCreateAssignment = async (assignmentData: Partial<DiscountAssignment>) => {
    if (!selectedDiscount) return;

    try {
      const assignmentPayload = {
        ...assignmentData,
        discountId: selectedDiscount.discountId,
        enterpriseId: selectedEnterpriseId,
        assignedBy: user?.getUsername?.() || 'system'
      };
      
      console.log('Creating assignment with payload:', assignmentPayload);
      
      const response = await authenticatedFetch(
        API_ENDPOINTS.createDiscountAssignment(),
        {
          method: 'POST',
          body: JSON.stringify(assignmentPayload)
        }
      );

      console.log('Assignment creation response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Assignment creation error response:', errorText);
        throw new Error(`Failed to create assignment: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Assignment creation response:', data);
      
      if (data.success && data.data) {
        setAssignments(prev => [data.data, ...prev]);
      }
      
      setShowAssignmentModal(false);
      setError(null);
      
      // Reload assignments to ensure we have the latest data
      await loadAssignments();
    } catch (err) {
      console.error('Assignment creation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create assignment');
    }
  };

  if (!canManageDiscounts) {
    return (
      <div className="discount-management-container">
        <div className="access-denied">
          <h3>Access Denied</h3>
          <p>You don't have permission to manage discounts.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="discount-management-container">
        <div className="loading-spinner">Loading discounts...</div>
      </div>
    );
  }

  return (
    <div className="discount-management-container" style={{ padding: '20px' }}>
      <div className="header-section" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Enterprise selection lives in the header strip — no in-page picker. */}
          <div />
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Create New Discount
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '15px', marginTop: '15px' }}>
          <select
            value={filter.status}
            onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={filter.triggerType}
            onChange={(e) => setFilter(prev => ({ ...prev, triggerType: e.target.value }))}
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="">All Trigger Types</option>
            <option value="manual">Manual</option>
            <option value="time_based">Time-Based</option>
            <option value="loyalty">Loyalty</option>
            <option value="ai_triggered">AI-Triggered</option>
          </select>

          <select
            value={filter.assignmentLevel}
            onChange={(e) => setFilter(prev => ({ ...prev, assignmentLevel: e.target.value }))}
            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="">All Assignment Levels</option>
            <option value="enterprise">Enterprise</option>
            <option value="concept">Concept</option>
            <option value="franchise">Franchise</option>
            <option value="store">Store</option>
            <option value="table">Table</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '10px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '15px'
        }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Discounts List */}
        <div style={{ flex: 1 }}>
          <h3>Discounts ({discounts.length})</h3>
          <div style={{ border: '1px solid #ddd', borderRadius: '5px' }}>
            {discounts.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No discounts found
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Name</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Value</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Authorization</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.map((discount) => (
                    <tr
                      key={discount.discountId}
                      style={{
                        backgroundColor: selectedDiscount?.discountId === discount.discountId ? '#e3f2fd' : 'white',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setSelectedDiscount(normalizeDiscount(discount));
                        loadAssignments(discount.discountId);
                      }}
                    >
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        <div>
                          <strong>{discount.name || 'Unnamed Discount'}</strong>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                            {discount.description || 'No description'}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {(discount.type || discount.discountType) ? (discount.type || discount.discountType).replace('_', ' ').toUpperCase() : 'N/A'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        {(discount.type || discount.discountType) === 'percentage' ? `${discount.value || discount.discountValue || 0}%` : `$${discount.value || discount.discountValue || 0}`}
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: (discount.status || 'inactive') === 'active' ? '#d4edda' : '#f8d7da',
                          color: (discount.status || 'inactive') === 'active' ? '#155724' : '#721c24',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {(discount.status || 'inactive').toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        {(discount.requiresAuthorization || false) ? (
                          <span style={{
                            padding: '4px 8px',
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            {discount.authorizationLevel.replace('_', ' ').toUpperCase()}
                          </span>
                        ) : (
                          <span style={{ color: '#666', fontSize: '12px' }}>None</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateDiscount(discount.discountId, {
                                status: discount.status === 'active' ? 'inactive' : 'active'
                              });
                            }}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              cursor: 'pointer'
                            }}
                          >
                            {discount.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteDiscount(discount.discountId);
                            }}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              border: '1px solid #dc3545',
                              borderRadius: '4px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              cursor: 'pointer'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Discount Details */}
        {selectedDiscount && (
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3>Discount Details</h3>
              <button
                onClick={() => setShowAssignmentModal(true)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Add Assignment
              </button>
            </div>

            <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '15px', backgroundColor: 'white' }}>
              <div style={{ marginBottom: '15px' }}>
                <h4>{selectedDiscount.name || 'Unnamed Discount'}</h4>
                <p style={{ margin: '5px 0', color: '#666' }}>{selectedDiscount.description || 'No description'}</p>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '15px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Type:</label>
                    <div>{(selectedDiscount.type || selectedDiscount.discountType) ? (selectedDiscount.type || selectedDiscount.discountType).replace('_', ' ').toUpperCase() : 'N/A'}</div>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Value:</label>
                    <div>{(selectedDiscount.type || selectedDiscount.discountType) === 'percentage' ? `${selectedDiscount.value || selectedDiscount.discountValue || 0}%` : `$${selectedDiscount.value || selectedDiscount.discountValue || 0}`}</div>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>GL Code:</label>
                    <div>{selectedDiscount.glCode || 'Not set'}</div>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Can Combine:</label>
                    <div>{selectedDiscount.canCombine ? 'Yes' : 'No'}</div>
                  </div>
                </div>

                {/* Time Restrictions */}
                {selectedDiscount.timeRestrictions && selectedDiscount.timeRestrictions.enabled && (
                  <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <h5 style={{ margin: '0 0 10px 0' }}>Time Restrictions</h5>
                    <div style={{ fontSize: '12px' }}>
                      {selectedDiscount.timeRestrictions && selectedDiscount.timeRestrictions.startTime && (
                        <div>Time: {selectedDiscount.timeRestrictions.startTime} - {selectedDiscount.timeRestrictions.endTime}</div>
                      )}
                      {selectedDiscount.timeRestrictions && selectedDiscount.timeRestrictions.daysOfWeek && selectedDiscount.timeRestrictions.daysOfWeek.length > 0 && (
                        <div>Days: {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
                          .filter((_, i) => selectedDiscount.timeRestrictions.daysOfWeek.includes(i))
                          .join(', ')}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Assignments */}
                <div style={{ marginTop: '15px' }}>
                  <h5>Assignments ({assignments.length})</h5>
                  {assignments.length === 0 ? (
                    <div style={{ color: '#666', fontSize: '12px' }}>No assignments</div>
                  ) : (
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {assignments.filter(assignment => assignment && assignment.assignmentId).map((assignment) => (
                        <div
                          key={assignment.assignmentId}
                          style={{
                            padding: '8px',
                            margin: '5px 0',
                            backgroundColor: assignment.isActive ? '#d4edda' : '#f8d7da',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                        >
                          <div><strong>{assignment.assignmentLevel?.toUpperCase() || 'UNKNOWN'}</strong>: {assignment.targetName || 'No Name'}</div>
                          <div style={{ color: '#666' }}>Priority: {assignment.priority || 1}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Discount Modal */}
      {showCreateModal && (
        <DiscountCreateModal
          onSave={handleCreateDiscount}
          onCancel={() => setShowCreateModal(false)}
        />
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && selectedDiscount && (
        <AssignmentCreateModal
          discount={selectedDiscount}
          enterpriseId={selectedEnterpriseId}
          onSave={handleCreateAssignment}
          onCancel={() => setShowAssignmentModal(false)}
        />
      )}
    </div>
  );
};

// Create Discount Modal Component
const DiscountCreateModal: React.FC<{
  onSave: (data: Partial<Discount>) => void;
  onCancel: () => void;
}> = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'percentage' as const,
    value: 0,
    glCode: '',
    requiresAuthorization: false,
    authorizationLevel: 'none' as const,
    canCombine: false,
    timeRestrictions: {
      enabled: false,
      startTime: '',
      endTime: '',
      daysOfWeek: [] as number[],
      dateRange: {
        start: null,
        end: null
      }
    },
    itemRestrictions: {
      enabled: false,
      restrictionType: 'none' as 'none' | 'items' | 'categories' | 'both',
      allowedItems: [] as string[],
      excludedItems: [] as string[],
      allowedCategories: [] as string[],
      excludedCategories: [] as string[],
      minimumItemQuantity: null as number | null,
      maximumItemQuantity: null as number | null
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        width: '500px',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <h3>Create New Discount</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              required
              rows={3}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed_amount">Fixed Amount</option>
                <option value="buy_one_get_one">BOGO</option>
                <option value="free_item">Free Item</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Value *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.value}
                onChange={(e) => setFormData(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                required
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              General Ledger Code
            </label>
            <input
              type="text"
              value={formData.glCode}
              onChange={(e) => setFormData(prev => ({ ...prev, glCode: e.target.value }))}
              placeholder="e.g., DISC001"
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={formData.requiresAuthorization}
                onChange={(e) => setFormData(prev => ({ ...prev, requiresAuthorization: e.target.checked }))}
              />
              <span>Requires Manager Authorization</span>
            </label>

            {formData.requiresAuthorization && (
              <select
                value={formData.authorizationLevel}
                onChange={(e) => setFormData(prev => ({ ...prev, authorizationLevel: e.target.value as any }))}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', marginTop: '8px' }}
              >
                <option value="manager">Manager</option>
                <option value="system_admin">System Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            )}
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={formData.canCombine}
                onChange={(e) => setFormData(prev => ({ ...prev, canCombine: e.target.checked }))}
              />
              <span>Can be combined with other discounts</span>
            </label>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={formData.timeRestrictions.enabled}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  timeRestrictions: { ...prev.timeRestrictions, enabled: e.target.checked }
                }))}
              />
              <span>Enable Time Restrictions</span>
            </label>

            {formData.timeRestrictions.enabled && (
              <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Start Time</label>
                    <input
                      type="time"
                      value={formData.timeRestrictions.startTime}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        timeRestrictions: { ...prev.timeRestrictions, startTime: e.target.value }
                      }))}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>End Time</label>
                    <input
                      type="time"
                      value={formData.timeRestrictions.endTime}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        timeRestrictions: { ...prev.timeRestrictions, endTime: e.target.value }
                      }))}
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Days of Week</label>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                      <label key={day} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                        <input
                          type="checkbox"
                          checked={formData.timeRestrictions.daysOfWeek.includes(index)}
                          onChange={(e) => {
                            const newDays = e.target.checked
                              ? [...formData.timeRestrictions.daysOfWeek, index]
                              : formData.timeRestrictions.daysOfWeek.filter(d => d !== index);
                            setFormData(prev => ({
                              ...prev,
                              timeRestrictions: { ...prev.timeRestrictions, daysOfWeek: newDays }
                            }));
                          }}
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Item and Category Restrictions Section */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={formData.itemRestrictions?.enabled || false}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  itemRestrictions: { 
                    ...prev.itemRestrictions, 
                    enabled: e.target.checked,
                    restrictionType: prev.itemRestrictions?.restrictionType || 'none',
                    allowedItems: prev.itemRestrictions?.allowedItems || [],
                    excludedItems: prev.itemRestrictions?.excludedItems || [],
                    allowedCategories: prev.itemRestrictions?.allowedCategories || [],
                    excludedCategories: prev.itemRestrictions?.excludedCategories || [],
                    minimumItemQuantity: prev.itemRestrictions?.minimumItemQuantity || null,
                    maximumItemQuantity: prev.itemRestrictions?.maximumItemQuantity || null
                  }
                }))}
              />
              <span>Limit to Specific Items or Categories</span>
            </label>

            {formData.itemRestrictions?.enabled && (
              <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>
                    Restriction Type
                  </label>
                  <select
                    value={formData.itemRestrictions?.restrictionType || 'none'}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      itemRestrictions: { 
                        ...prev.itemRestrictions,
                        enabled: true,
                        restrictionType: e.target.value as any,
                        allowedItems: prev.itemRestrictions?.allowedItems || [],
                        excludedItems: prev.itemRestrictions?.excludedItems || [],
                        allowedCategories: prev.itemRestrictions?.allowedCategories || [],
                        excludedCategories: prev.itemRestrictions?.excludedCategories || [],
                        minimumItemQuantity: prev.itemRestrictions?.minimumItemQuantity || null,
                        maximumItemQuantity: prev.itemRestrictions?.maximumItemQuantity || null
                      }
                    }))}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="none">No Restrictions</option>
                    <option value="items">Specific Menu Items</option>
                    <option value="categories">Specific Categories</option>
                    <option value="both">Both Items and Categories</option>
                  </select>
                </div>

                {(formData.itemRestrictions?.restrictionType === 'items' || formData.itemRestrictions?.restrictionType === 'both') && (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>
                      Menu Items (Multi-select available items)
                    </label>
                    <div style={{ 
                      border: '1px solid #ddd', 
                      borderRadius: '4px', 
                      padding: '8px', 
                      backgroundColor: 'white',
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      Menu items will be loaded when enterprise is selected. Select items to restrict this discount to specific menu items.
                    </div>
                  </div>
                )}

                {(formData.itemRestrictions?.restrictionType === 'categories' || formData.itemRestrictions?.restrictionType === 'both') && (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>
                      Categories (Multi-select available categories)
                    </label>
                    <div style={{ 
                      border: '1px solid #ddd', 
                      borderRadius: '4px', 
                      padding: '8px', 
                      backgroundColor: 'white',
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      Categories will be loaded when enterprise is selected. Select categories to restrict this discount to items in specific categories.
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Min Item Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.itemRestrictions?.minimumItemQuantity || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        itemRestrictions: { 
                          ...prev.itemRestrictions,
                          enabled: true,
                          restrictionType: prev.itemRestrictions?.restrictionType || 'none',
                          allowedItems: prev.itemRestrictions?.allowedItems || [],
                          excludedItems: prev.itemRestrictions?.excludedItems || [],
                          allowedCategories: prev.itemRestrictions?.allowedCategories || [],
                          excludedCategories: prev.itemRestrictions?.excludedCategories || [],
                          minimumItemQuantity: e.target.value ? parseInt(e.target.value) : null,
                          maximumItemQuantity: prev.itemRestrictions?.maximumItemQuantity || null
                        }
                      }))}
                      placeholder="e.g., 2"
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Max Item Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.itemRestrictions?.maximumItemQuantity || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        itemRestrictions: { 
                          ...prev.itemRestrictions,
                          enabled: true,
                          restrictionType: prev.itemRestrictions?.restrictionType || 'none',
                          allowedItems: prev.itemRestrictions?.allowedItems || [],
                          excludedItems: prev.itemRestrictions?.excludedItems || [],
                          allowedCategories: prev.itemRestrictions?.allowedCategories || [],
                          excludedCategories: prev.itemRestrictions?.excludedCategories || [],
                          minimumItemQuantity: prev.itemRestrictions?.minimumItemQuantity || null,
                          maximumItemQuantity: e.target.value ? parseInt(e.target.value) : null
                        }
                      }))}
                      placeholder="e.g., 5"
                      style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Create Discount
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Assignment Create Modal Component
const AssignmentCreateModal: React.FC<{
  discount: Discount;
  enterpriseId: string;
  onSave: (data: Partial<DiscountAssignment>) => void;
  onCancel: () => void;
}> = ({ discount, enterpriseId, onSave, onCancel }) => {
  // Removed session since we're now using authenticatedFetch
  const [formData, setFormData] = useState({
    assignmentLevel: 'store' as 'concept' | 'franchise' | 'org_unit' | 'store' | 'table',
    targetId: '',
    targetName: '',
    priority: 0,
    restrictions: {},
    
    // Effective date functionality
    effectiveDate: new Date().toISOString().slice(0, 16), // Current datetime
    expirationDate: '',
    timezone: 'UTC',
    
    // Hierarchy inheritance
    inheritFromParent: false,
    overrideParent: false
  });

  // State for hierarchy data
  const [concepts, setConcepts] = useState<any[]>([]);
  const [franchisees, setFranchisees] = useState<any[]>([]);
  const [orgUnits, setOrgUnits] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [targetError, setTargetError] = useState<string | null>(null);

  // Load targets when assignment level changes
  useEffect(() => {
    loadTargetsForLevel(formData.assignmentLevel);
  }, [formData.assignmentLevel]);

  const loadTargetsForLevel = async (level: string) => {
    setLoadingTargets(true);
    setTargetError(null);
    console.log('Loading targets for level:', level, 'Enterprise ID:', enterpriseId);
    
    try {
      switch (level) {
        case 'concept':
          const conceptsResponse = await authenticatedFetch(API_ENDPOINTS.concepts(enterpriseId));
          if (!conceptsResponse.ok) throw new Error('Failed to load concepts');
          const conceptsData = await conceptsResponse.json();
          setConcepts(conceptsData.data || conceptsData.concepts || []);
          break;
          
        case 'franchise':
          const franchiseesResponse = await authenticatedFetch(API_ENDPOINTS.franchisees(enterpriseId));
          if (!franchiseesResponse.ok) throw new Error('Failed to load franchisees');
          const franchiseesData = await franchiseesResponse.json();
          setFranchisees(franchiseesData.data || franchiseesData.franchisees || []);
          break;
          
        case 'org_unit':
          // Organizational Units endpoint doesn't exist in Core Data API
          // Use franchisees as organizational units for now
          const orgUnitsResponse = await authenticatedFetch(API_ENDPOINTS.franchisees(enterpriseId));
          if (!orgUnitsResponse.ok) throw new Error('Failed to load organizational units');
          const orgUnitsData = await orgUnitsResponse.json();
          setOrgUnits(orgUnitsData.data || orgUnitsData.franchisees || []);
          break;
          
        case 'store':
          const storesUrl = API_ENDPOINTS.enterpriseStores(enterpriseId);
          console.log('Fetching stores from:', storesUrl);
          const storesResponse = await authenticatedFetch(storesUrl);
          console.log('Stores response status:', storesResponse.status);
          if (!storesResponse.ok) throw new Error('Failed to load stores');
          const storesData = await storesResponse.json();
          console.log('Stores data received:', storesData);
          console.log('storesData.data:', storesData.data);
          console.log('storesData.stores:', storesData.stores);
          setStores(storesData.data || storesData.stores || []);
          console.log('Stores set to:', storesData.data || storesData.stores || []);
          break;
          
        case 'table':
          // For tables, we would need to select a store first
          // This is a simplified version - in production, you'd have a store selector
          setTargetError('Please select a store first, then tables will be available');
          break;
      }
      
      // Reset target selection when level changes
      setFormData(prev => ({ ...prev, targetId: '', targetName: '' }));
      
    } catch (err) {
      console.error('Error loading targets:', err);
      setTargetError(err instanceof Error ? err.message : 'Failed to load targets');
    } finally {
      setLoadingTargets(false);
    }
  };

  const getTargetOptions = () => {
    const options = (() => {
      switch (formData.assignmentLevel) {
        case 'concept':
          return concepts;
        case 'franchise':
          return franchisees;
        case 'org_unit':
          return orgUnits;
        case 'store':
          return stores;
        default:
          return [];
      }
    })();
    console.log('getTargetOptions for level:', formData.assignmentLevel, 'returning:', options);
    if (options.length > 0) {
      console.log('First option structure:', options[0]);
      console.log('Properties available:', Object.keys(options[0]));
    }
    return options;
  };

  const handleTargetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const options = getTargetOptions();
    const selected = options.find(opt => opt.id === selectedId || opt.storeId === selectedId);
    
    console.log('handleTargetChange:', { selectedId, selected, selectedName: selected?.name });
    console.log('Available name properties:', {
      name: selected?.name,
      storeName: selected?.storeName,
      title: selected?.title,
      displayName: selected?.displayName
    });
    
    const targetName = selected?.name || selected?.storeName || selected?.title || selected?.displayName || '';
    
    setFormData(prev => ({
      ...prev,
      targetId: selectedId,
      targetName: targetName
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        width: '500px',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h3>Assign Discount: {discount.name}</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Assignment Level *
            </label>
            <select
              value={formData.assignmentLevel}
              onChange={(e) => setFormData(prev => ({ ...prev, assignmentLevel: e.target.value as any }))}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="concept">Concept</option>
              <option value="franchise">Franchise</option>
              <option value="org_unit">Organizational Unit</option>
              <option value="store">Store</option>
              <option value="table">Table</option>
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Target *
            </label>
            {loadingTargets ? (
              <div style={{ padding: '8px', color: '#666' }}>Loading...</div>
            ) : targetError ? (
              <div style={{ padding: '8px', color: '#dc3545', fontSize: '14px' }}>{targetError}</div>
            ) : (
              <select
                value={formData.targetId}
                onChange={handleTargetChange}
                required
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                disabled={getTargetOptions().length === 0}
              >
                <option value="">Select a {formData.assignmentLevel.replace('_', ' ')}</option>
                {getTargetOptions().map(option => (
                  <option key={option.id || option.storeId} value={option.id || option.storeId}>
                    {option.name} {option.storeCode ? `(${option.storeCode})` : ''}
                  </option>
                ))}
              </select>
            )}
            {formData.targetName && (
              <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                Selected: {formData.targetName}
              </small>
            )}
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Priority
            </label>
            <input
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            <small style={{ color: '#666' }}>Higher numbers have higher priority</small>
          </div>

          {/* Effective Date Configuration */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Effective Date *
            </label>
            <input
              type="datetime-local"
              value={formData.effectiveDate}
              onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
              required
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            <small style={{ color: '#666' }}>When this assignment becomes active</small>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Expiration Date
            </label>
            <input
              type="datetime-local"
              value={formData.expirationDate}
              onChange={(e) => setFormData(prev => ({ ...prev, expirationDate: e.target.value }))}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            <small style={{ color: '#666' }}>Leave empty for no expiration</small>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Timezone
            </label>
            <select
              value={formData.timezone}
              onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
            </select>
          </div>

          {/* Hierarchy Inheritance Options */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={formData.inheritFromParent}
                onChange={(e) => setFormData(prev => ({ ...prev, inheritFromParent: e.target.checked }))}
              />
              <span>Inherit from parent hierarchy level</span>
            </label>
            <small style={{ color: '#666', display: 'block', marginLeft: '28px' }}>
              This assignment will also apply to child levels in the hierarchy
            </small>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={formData.overrideParent}
                onChange={(e) => setFormData(prev => ({ ...prev, overrideParent: e.target.checked }))}
              />
              <span>Override parent assignments</span>
            </label>
            <small style={{ color: '#666', display: 'block', marginLeft: '28px' }}>
              This assignment takes precedence over parent hierarchy assignments
            </small>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Create Assignment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DiscountManagement;